// src/utils/api/constants.ts

// Enhanced request timeouts with more granular control
export const API_TIMEOUTS = {
    DEFAULT: 30000,     // 30 seconds
    LONG: 60000,        // 60 seconds for complex operations
    SHORT: 10000,       // 10 seconds for simple operations
    MIN: 5000,          // 5 seconds minimum
    MAX: 120000,        // 2 minutes maximum
    HOOK_DEFAULT: 20000, // Default timeout for hook operations
    CLEANUP_INTERVAL: 60000 // 1 minute
} as const;

// Enhanced retry configuration
export const RETRY_CONFIG = {
    MAX_RETRIES: 3,
    INITIAL_DELAY: 1000,    // 1 second
    MAX_DELAY: 10000,       // 10 seconds
    BACKOFF_FACTOR: 2,      // Exponential backoff multiplier
    JITTER_MAX: 200,        // Maximum random delay addition
    HOOK_MAX_RETRIES: 2     // Default retries for hooks
} as const;

// Enhanced request tracking configuration
export const REQUEST_TRACKING = {
    MAX_PENDING: 10,
    STALE_THRESHOLD: 30000,   // 30 seconds
    CLEANUP_INTERVAL: 10000,  // 10 seconds
    MAX_AGE: 60000,          // 1 minute
    HOOK_TRACK_WINDOW: 5000  // Window for tracking hook requests
} as const;

// Enhanced request deduplication settings
export const DEDUPLICATION = {
    WINDOW: 1000,            // 1 second window
    HASH_LENGTH: 8,          // Length of request hash
    CLEANUP_THRESHOLD: 100,  // Maximum number of cached request hashes
    HOOK_WINDOW: 2000       // Deduplication window for hooks
} as const;

// Cache configuration
export const CACHE_CONFIG = {
    DEFAULT_TTL: 5 * 60 * 1000,    // 5 minutes
    MIN_TTL: 1000,                 // 1 second
    MAX_TTL: 30 * 60 * 1000,      // 30 minutes
    MAX_ENTRIES: 1000,             // Maximum cache entries
    CLEANUP_INTERVAL: 60 * 1000    // 1 minute cleanup interval
} as const;

// Hook-specific configuration
export const HOOK_CONFIG = {
    CACHE: {
        DEFAULT_TTL: 5 * 60 * 1000,    // 5 minutes
        MIN_TTL: 1000,                 // 1 second
        MAX_TTL: 30 * 60 * 1000,      // 30 minutes
        STALE_TIME: 30 * 1000,        // 30 seconds
        REVALIDATE_AFTER: 10 * 1000   // 10 seconds
    },
    SUSPENSE: {
        TIMEOUT: 5000,                // 5 seconds
        DELAY: 200                    // 200ms delay before showing loading
    },
    POLLING: {
        MIN_INTERVAL: 1000,           // 1 second
        MAX_INTERVAL: 30 * 1000,      // 30 seconds
        DEFAULT_INTERVAL: 5000        // 5 seconds
    },
    BATCH: {
        MAX_SIZE: 10,                 // Maximum batch size
        WINDOW: 50                    // 50ms batching window
    }
} as const;

// Valid status codes for response validation
export const VALID_STATUS_CODES = new Set([
    200, 201, 202, 203, 204, 206, 207
]);

// Enhanced default headers
export const DEFAULT_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
} as const;

// Enhanced error messages
export const ERROR_MESSAGES = {
    NETWORK: 'A network error occurred. Please check your connection and try again',
    TIMEOUT: 'The request timed out. Please try again',
    INVALID_STATUS: 'The server returned an invalid response status',
    PARSE_ERROR: 'Failed to parse the server response',
    ABORTED: 'The request was cancelled',
    TOO_MANY_REQUESTS: 'Too many requests are currently pending',
    CACHE_ERROR: 'Cache operation failed',
    DUPLICATE_REQUEST: 'A duplicate request was detected',
    VALIDATION_ERROR: 'Response validation failed',
    UNAUTHORIZED: 'Authentication is required to access this resource',
    FORBIDDEN: 'You do not have permission to access this resource',
    NOT_FOUND: 'The requested resource was not found',
    SERVER_ERROR: 'An internal server error occurred',
    HOOK_ERROR: 'An error occurred while processing the request'
} as const;

// Enhanced validation configuration
export const VALIDATION = {
    DEFAULT_STATUS_VALIDATOR: (status: number) => VALID_STATUS_CODES.has(status),
    CONTENT_TYPES: {
        JSON: 'application/json',
        TEXT: 'text/plain',
        HTML: 'text/html',
        FORM: 'application/x-www-form-urlencoded',
        MULTIPART: 'multipart/form-data'
    },
    METHODS: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'],
    MAX_CONTENT_LENGTH: 50 * 1024 * 1024  // 50MB
} as const;

// Environment-specific configuration
export const ENV_CONFIG = {
    IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
    ENABLE_LOGGING: process.env.REACT_APP_API_LOGGING === 'true',
    API_VERSION: process.env.REACT_APP_API_VERSION || 'v1',
    ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development'
} as const;

// Debug and logging configuration
export const DEBUG_CONFIG = {
    LOG_LEVELS: {
        ERROR: 'error',
        WARN: 'warn',
        INFO: 'info',
        DEBUG: 'debug'
    },
    MAX_LOG_SIZE: 1000,
    PERSIST_LOGS: true,
    REDACT_SENSITIVE_DATA: true,
    SENSITIVE_HEADERS: [
        'Authorization',
        'Cookie',
        'Set-Cookie'
    ]
} as const;